import React, { useState } from 'react';
import styles from './SvgArrow.module.css';
import classNames from 'classnames';
import renderIf from 'render-if';

// Arrow styles
const ARROW_STYLES = {
  snakeBackwards: 0,
  pointingDown: 1,
  pointingUp: 2,
  curvyForward: 3,
  straightForward: 4,
  loopBackwards: 5,
} as const;

type ArrowStylesValues = (typeof ARROW_STYLES)[keyof typeof ARROW_STYLES];

// Margin of empty space around the line on all sides.
// This prevents the arrowhead or line from being cut off.
const SVG_MARGIN = 5;

// Size of the arrowhead.
const ARROW_SIZE = 10;

// Size of the 'X' to remove the line.
const X_SIZE = 5;

// Amount of horizontal spacing from the start point
// before we break and start moving up or down.
const FORWARDS_SPACING = 10;

// Radius of the corners. Should be greater than both FORWARDS_SPACING.
const ARC_RADIUS = 8;

// The amount of space the arrow has before the bar at the end arrow.
const BACKWARDS_SPACING = 30 + X_SIZE;

// Padding for the remove button/square.
const REMOVE_PADDING = 8;

// Left offset for the remove button/square from the arrow.
const REMOVE_OFFSET = 10;

const REMOVE_CIRCLE_RADIUS = 10;

// Extra spacing to place arrows where we want them
// when using the 2-strokes arrow
const POINTING_DOWN_SPACING_X = 5;

function determineArrowStyle(startX, endX, startY, endY) {
  // If the two bars are vertically aligned.
  if (Math.abs(startY - endY) < 1) {
    if (endX - startX > 0) {
      return ARROW_STYLES['straightForward'];
    } else {
      return ARROW_STYLES['loopBackwards'];
    }
  }

  if (endX - startX < 0) {
    // When the end point is before the start point (or just after the start)
    return ARROW_STYLES['snakeBackwards'];
  } else if (endX - startX < FORWARDS_SPACING + BACKWARDS_SPACING) {
    // When the end point is after but close to the start point
    if (startY < endY) {
      return ARROW_STYLES['pointingDown'];
    } else {
      return ARROW_STYLES['pointingUp'];
    }
  } else {
    // When the end is further after the start
    return ARROW_STYLES['curvyForward'];
  }
}

function removeButton(
  startX: number,
  endX: number,
  endY: number,
  verticalArrowSpacingHeight: number,
  arrowStyle: ArrowStylesValues,
  onRemove: (event: React.MouseEvent<SVGCircleElement, MouseEvent>) => void
) {
  // Calculate positioning for the remove 'X'
  let removeX, removeY;

  if (arrowStyle === ARROW_STYLES['pointingDown']) {
    removeX = Math.max(
      startX + FORWARDS_SPACING - X_SIZE / 2,
      endX - X_SIZE / 2 + POINTING_DOWN_SPACING_X
    );
    removeY = endY - verticalArrowSpacingHeight - REMOVE_PADDING * 2 - X_SIZE;
  } else if (arrowStyle === ARROW_STYLES['pointingUp']) {
    removeX = Math.max(
      startX + FORWARDS_SPACING - X_SIZE / 2,
      endX - X_SIZE / 2 + POINTING_DOWN_SPACING_X
    );
    removeY = endY + verticalArrowSpacingHeight + REMOVE_PADDING * 2 + X_SIZE;
  } else {
    removeX = endX - (X_SIZE + 2 * REMOVE_OFFSET);
    removeY = endY - REMOVE_PADDING / 2;
  }

  const removeRect = (
    <circle
      cx={removeX + 5}
      cy={removeY + 3}
      r={REMOVE_CIRCLE_RADIUS}
      className={styles.removeWrapper}
      style={{ fill: 'rgb(255, 255, 255)' }}
      aria-label='Remove dependency'
      data-title='Remove dependency'
      onClick={onRemove}
    />
  );

  // The path for the foreignObject is from fa-solid fa-trash-can
  return (
    <React.Fragment>
      {removeRect}
      <foreignObject
        x={removeX}
        y={removeY - 4}
        width='10'
        height='12'
        className={styles.remove}
      >
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'>
          <path d='M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z' />
        </svg>
      </foreignObject>
    </React.Fragment>
  );
}

function buildLines(
  id: string | number,
  arrowStyle: ArrowStylesValues,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  inBetweenY: number,
  verticalArrowSpacingHeight: number,
  arrowColor: string
) {
  // After remove the feature flag, swap this into FORWARDS_SPACING
  const forwardsSpacing = 12;
  const arcYOffset = startY > endY ? -ARC_RADIUS : ARC_RADIUS;

  let pathData: string;

  switch (arrowStyle) {
    case ARROW_STYLES['snakeBackwards']:
      // [from point] -
      //               |
      //         ------
      //         |
      //         -> [to point]
      pathData = `
        M ${startX} ${startY}
        l ${forwardsSpacing - ARC_RADIUS} 0
        q ${ARC_RADIUS} 0 ${ARC_RADIUS} ${arcYOffset}
        L ${startX + forwardsSpacing} ${inBetweenY - arcYOffset}
        q 0 ${arcYOffset}, ${-ARC_RADIUS} ${arcYOffset}
        L ${endX - BACKWARDS_SPACING + ARC_RADIUS} ${inBetweenY}
        q ${-ARC_RADIUS} 0 ${-ARC_RADIUS} ${arcYOffset}
        L ${endX - BACKWARDS_SPACING} ${endY - arcYOffset}
        q 0 ${arcYOffset}, ${ARC_RADIUS} ${arcYOffset}
        L ${endX - 3} ${endY}
      `;
      break;

    case ARROW_STYLES['pointingDown']: {
      // [from point] ---
      //                |
      //                v
      //               [to point]

      // The arrow moves up at least at startX + forwardsSpacing
      const downStrokeX = Math.max(
        startX + forwardsSpacing,
        endX + POINTING_DOWN_SPACING_X
      );

      pathData = `
        M ${startX} ${startY}
        L ${downStrokeX - ARC_RADIUS} ${startY}
        q ${ARC_RADIUS} 0, ${ARC_RADIUS} ${ARC_RADIUS}
        L ${downStrokeX} ${endY - verticalArrowSpacingHeight}
      `;
      break;
    }

    case ARROW_STYLES['pointingUp']: {
      //               [to point]
      //                A
      //                |
      // [from point] ---

      // The arrow moves up at least at startX + forwardsSpacing
      const upStrokeX = Math.max(
        startX + forwardsSpacing,
        endX + POINTING_DOWN_SPACING_X
      );

      pathData = `
        M ${startX} ${startY}
        L ${upStrokeX - ARC_RADIUS} ${startY}
        q ${ARC_RADIUS} 0, ${ARC_RADIUS} ${-ARC_RADIUS}
        L ${upStrokeX} ${endY + verticalArrowSpacingHeight}
      `;
      break;
    }

    case ARROW_STYLES['curvyForward']:
      // [from point] -
      //               |
      //               ----> [to point]
      pathData = `
        M ${startX} ${startY}
        l ${forwardsSpacing - ARC_RADIUS} 0
        q ${ARC_RADIUS} 0, ${ARC_RADIUS} ${arcYOffset}
        L ${startX + forwardsSpacing} ${endY - arcYOffset}
        q 0 ${arcYOffset}, ${ARC_RADIUS} ${arcYOffset}
        L ${endX - 3} ${endY}
      `;
      break;

    case ARROW_STYLES['straightForward']:
      // [from point] -----> [to point]
      pathData = `
        M ${startX} ${startY}
        L ${endX - 3} ${endY}
      `;
      break;

    case ARROW_STYLES['loopBackwards']: {
      const middleHeight = Math.abs(inBetweenY);
      //   -> [to point] [from point] -
      //  |                            |
      //   ----------------------------
      pathData = `
        M ${startX} ${startY}
        l ${forwardsSpacing - ARC_RADIUS} 0
        q ${ARC_RADIUS} 0, ${ARC_RADIUS} ${ARC_RADIUS}
        l 0, ${middleHeight - ARC_RADIUS}
        q 0 ${ARC_RADIUS}, ${-ARC_RADIUS} ${ARC_RADIUS}
        L ${endX - BACKWARDS_SPACING}, ${
        endY + 2 * ARC_RADIUS + middleHeight - ARC_RADIUS
      }
        q ${-ARC_RADIUS} 0, ${-ARC_RADIUS} ${-ARC_RADIUS}
        l 0, ${-middleHeight + ARC_RADIUS}
        q 0 ${-ARC_RADIUS}, ${ARC_RADIUS} ${-ARC_RADIUS}
        L ${endX - 3} ${endY}
      `;
      break;
    }
  }

  // Two paths, one is the actual line, while the other is a thicker invisible
  // version used for hovering.
  return (
    <>
      <path
        d={pathData}
        stroke='transparent'
        strokeWidth='8'
        fill='transparent'
      />
      <path
        d={pathData}
        stroke={arrowColor}
        strokeWidth='2'
        fill='transparent'
        markerEnd={`url(#arrow-${id})`}
      />
    </>
  );
}
interface Point {
  x: number;
  y: number;
}
interface SvgArrowProps {
  // unique identifier for this line (needed for rendering)
  id: string;
  startPoint: Point;
  endPoint: Point;
  // How far to move the arrow end down/up when it ends vertically. In other
  // words, how far away from the end Y does the arrow end. For example, if
  // end Y is in the middle of a bar, then this should be half the bar height.
  verticalArrowSpacingHeight: number;
  // If we need to draw the 5-stroke line below, this is the vertical distance
  // between the start Y location and the backstroke line.
  centerOffset: number;
  color: string;
  hoverColor: string;
  // Whether to render a clickable remove 'X' for deleting the line
  isRemovable?: boolean;
  onRemove?: () => void;
  ariaLabel?: string;
  filter?: boolean;
}

// Component to render an SVG arrow, optionally removable
// (which renders an X near the arrowhead that calls the
// onRemove function when clicked).
const SvgArrow: React.FC<SvgArrowProps> = ({
  id,
  startPoint,
  endPoint,
  color,
  hoverColor,
  verticalArrowSpacingHeight,
  centerOffset = 25,
  isRemovable = false,
  onRemove = null,
  ariaLabel = null,
  filter = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  let arrowColor = isHovered ? hoverColor : color;

  // Find the x/y coords of the SVG container.
  const containerLeft = Math.min(startPoint.x, endPoint.x) - BACKWARDS_SPACING;
  const containerTop = Math.min(startPoint.y, endPoint.y);

  const removePadding = REMOVE_PADDING;
  const removeOffset = REMOVE_OFFSET;

  // Adjust the points for the margin. These points are relative
  // to the container (i.e. 0, 0 is the top left point of the SVG).
  const startX = startPoint.x + SVG_MARGIN + removeOffset - containerLeft;
  const endX = endPoint.x + SVG_MARGIN + removeOffset - containerLeft;
  const startY = startPoint.y + SVG_MARGIN + removePadding - containerTop;
  const endY = endPoint.y + SVG_MARGIN + removePadding - containerTop;

  // Three options for what strokes we render
  const arrowStyle = determineArrowStyle(startX, endX, startY, endY);

  var svgHeight =
    Math.abs(endPoint.y - startPoint.y) + SVG_MARGIN * 2 + removePadding * 2;

  if (arrowStyle === ARROW_STYLES['loopBackwards']) {
    svgHeight += 2 * ARC_RADIUS;
  }

  // Position the SVG container.
  const svgStyle = {
    left: containerLeft - SVG_MARGIN - removeOffset,
    top: containerTop - SVG_MARGIN - removePadding,
    height: svgHeight,
    width:
      Math.abs(Math.max(startPoint.x, endPoint.x) - containerLeft) +
      FORWARDS_SPACING +
      SVG_MARGIN * 2 +
      removeOffset +
      X_SIZE,
    filter: filter ? 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.2)' : '',
  };

  // inBetweenY is the position of the in-between
  // line for a "backwards" (5-stroke) line.
  // overlapY is to "overlap" the vertical strokes so
  // the lines aren't disconnected at the corners.
  let inBetweenY;
  if (startY < endY) {
    inBetweenY = startY + centerOffset;
  } else {
    inBetweenY = startY - centerOffset;
  }

  const lines = buildLines(
    id,
    arrowStyle,
    startX,
    startY,
    endX,
    endY,
    inBetweenY,
    verticalArrowSpacingHeight,
    arrowColor
  );

  // SVG path to draw the arrowhead.
  const arrowPath = [
    // Move to bottom point of arrow
    'M 0 0',
    // Line to right tip of arrow
    `L ${ARROW_SIZE} ${ARROW_SIZE / 2}`,
    // Line to top point of arrow
    `L 0 ${ARROW_SIZE}`,
    // Line back to origin (bottom point)
    'z',
  ];

  return (
    <svg
      className={classNames(styles.container, {
        [styles.hovered]: isHovered,
      })}
      style={svgStyle}
      aria-label={ariaLabel || 'arrow'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <defs>
        <marker
          id={`arrow-${id}`}
          viewBox={`0 0 ${ARROW_SIZE} ${ARROW_SIZE}`}
          refX={ARROW_SIZE / 2}
          refY={ARROW_SIZE / 2}
          markerWidth='3'
          markerHeight='3'
          orient='auto-start-reverse'
        >
          <path
            d={arrowPath.join(' ')}
            // Fill color of the arrowhead at the
            // end of the line.
            fill={arrowColor}
          />
        </marker>
      </defs>

      {lines}

      {renderIf(isRemovable)(
        removeButton(
          startX,
          endX,
          endY,
          verticalArrowSpacingHeight,
          arrowStyle,
          onRemove
        )
      )}
    </svg>
  );
};

export default SvgArrow;
